import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useAuthContext } from '../../Context/AuthContext/AuthContext';
import { useDuplicateCertificateQuery } from '../../rest/useDuplicateCertificate';
import CommonOfflinePaymentInfo from '../new-member-applications/IndexFile/paymentInformation/offline/CommonOfflinePaymentInfo';
import axios from 'axios';
import { url } from '../../Helpers/utils/Constant';
import { getToken } from '../../rest/utils/get-token';
import { showToast } from '../../Toaster/Toaster';
import ImageUploaderWithPreview from '../../Common/ImageUploaderWithPreview';
import { API_ENDPOINTS } from '../../rest/utils/api-endpoints';

type IProps = {
  handelClose: Dispatch<SetStateAction<boolean>>;
  setReload: () => void;
};

const CreateDuplicateApplication = ({ handelClose, setReload }: IProps) => {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const { data: duplicateDraftList, fetchData: fetchDuplicateDraft } =
    useDuplicateCertificateQuery();
  const draftData = duplicateDraftList?.[0];

  useEffect(() => {
    if (user?.user_member_id) {
      fetchDuplicateDraft({
        duplicate_certificate_application_member_id: user.user_member_id,
        duplicate_certificate_application_status: 'draft',
      });
    }
  }, [user?.user_member_id, fetchDuplicateDraft]);

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors },
    setValue,
    control,
    watch,
    getValues,
  } = useForm<any>({
    defaultValues: {
      bank_ac_no: '00890320000141',
      amount: '',
      bank_name: 'Trust Bank (Kakrail Branch)',
    },
  });

  const amount = watch('amount');
  const [loading, setLoading] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const token = getToken();

  useEffect(() => {
    axios
      .get(
        `${url}/api/atab/payment-amount?application_type=DUPLICATE_CERTIFICATE`,
      )
      .then((res) => {
        if (res.data.success) {
          setValue('amount', res.data.data.amount);
        }
      });
  }, [setValue]);

  const onSubmit = async (data: any, asDraft: boolean = false) => {
    const formData = new FormData();

    const paymentFields = [
      'type',
      'bank_ac_no',
      'amount',
      'bank_name',
      'deposit_date',
      'branch_name',
      'reference_no',
      'bank_slip',
    ];

    if (asDraft) {
      formData.append('status', 'draft');
      formData.append('duplicate_certificate_application_status', 'draft');
    } else {
      if (data?.type === 'online') {
        formData.append(
          'success_page',
          `${window.location.origin}/payment-success`,
        );
        formData.append(
          'failed_page',
          `${window.location.origin}/payment-failed`,
        );
        formData.append(
          'cancelled_page',
          `${window.location.origin}/payment-reject`,
        );
      }
    }

    Object.keys(data).forEach((item) => {
      if (asDraft && paymentFields.includes(item)) {
        return;
      }
      if (!asDraft && data?.type !== 'offline' && paymentFields.includes(item) && item !== 'type') {
        return;
      }

      if (typeof data[item] === 'object' && data[item] !== null) {
        if (data[item].length > 0) {
          formData.append(item, data[item][0]);
        }
      } else if (
        data[item] !== undefined &&
        data[item] !== null &&
        data[item] !== ''
      ) {
        formData.append(item, data[item]);
      }
    });

    formData.append(
      'duplicate_certificate_application_member_id',
      user.user_member_id?.toString(),
    );

    try {
      setLoading(true);
      setIsDraft(asDraft);
      const response = await fetch(`${url}/api/member/duplicate-certificate-application`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
        },
        body: formData,
      });

      const responseData = await response.json();

      if (responseData.success) {
        if (!asDraft && responseData?.data?.redirect_url) {
          window.open(
            responseData?.data?.redirect_url,
            '_blank',
            'noopener,noreferrer',
          );
        }
        showToast(
          responseData.message ||
          (asDraft
            ? 'Application saved as draft successfully'
            : 'Application submitted successfully'),
          'success',
        );
        queryClient.invalidateQueries([API_ENDPOINTS.DUPLICATE_CERTIFICATE]);
        handelClose(false);
        setReload();
      } else {
        setLoading(false);
        setIsDraft(false);
        showToast(
          responseData.message ||
          (asDraft
            ? 'Failed to save application as draft'
            : 'Failed to submit application'),
          'error',
        );
      }
    } catch (error: any) {
      showToast(error?.message || 'An error occurred', 'error');
      setLoading(false);
      setIsDraft(false);
    }
  };

  const handleSaveDraft = () => {
    const values = getValues();
    onSubmit(values, true);
  };

  return (
    <Box
      component='form'
      onSubmit={handleSubmit((data) => onSubmit(data, false))}
      p={2}
    >
      <Grid container spacing={2} mb={2}>
        <Grid xs={12} sm={6} md={6} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Duplicate Certificate GD Copy ( max size 5mb )'
            fileName='duplicate_certificate_application_gd_copy'
            required
            imgSerUrl={
              draftData?.duplicate_certificate_application_gd_copy || null
            }
          />
        </Grid>
        <Grid xs={12} sm={6} md={6} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Duplicate Certificate Application file ( max size 5mb )'
            fileName='duplicate_certificate_application_application_file'
            required
            imgSerUrl={
              draftData?.duplicate_certificate_application_application_file ||
              null
            }
          />
        </Grid>
        <Grid xs={12} sm={6} md={6} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Duplicate Certificate Proof of Submission of Tax Return (max size 5mb)'
            fileName='duplicate_certificate_application_tax_return'
            required
            imgSerUrl={
              draftData?.duplicate_certificate_application_tax_return || null
            }
          />
        </Grid>
        <Grid xs={12} sm={6} md={6} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Duplicate Certificate trade license file ( max size 5mb )'
            fileName='duplicate_certificate_application_trade_license'
            required
            imgSerUrl={
              draftData?.duplicate_certificate_application_trade_license || null
            }
          />
        </Grid>
        <Grid xs={12} sm={6} md={6} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Duplicate Certificate civil aviation file ( max size 5mb )'
            fileName='duplicate_certificate_application_civil_aviation'
            required
            imgSerUrl={
              draftData?.duplicate_certificate_application_civil_aviation || null
            }
          />
        </Grid>

        <Grid xs={12} item sx={{ my: 2 }}>
          <Divider>
            <Chip label='Payment Information' size='small' />
          </Divider>
        </Grid>
        <Grid xs={12} item>
          <CommonOfflinePaymentInfo
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            register={register}
            setValue={setValue}
            control={control}
            onlinePaymentAmount={amount}
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={2}
        justifyContent='flex-end'
        alignItems='center'
        mt={4}
      >
        <Button
          type='button'
          variant='outlined'
          color='primary'
          disabled={loading}
          onClick={handleSaveDraft}
          startIcon={
            loading && isDraft ? (
              <CircularProgress size={18} color='inherit' />
            ) : null
          }
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: 160,
            py: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          <span>{loading && isDraft ? 'Saving Draft...' : 'Save As Draft'}</span>
        </Button>

        <Button
          type='submit'
          variant='contained'
          color='primary'
          disabled={loading}
          disableElevation
          startIcon={
            loading && !isDraft ? (
              <CircularProgress size={18} color='inherit' />
            ) : null
          }
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: 160,
            py: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          <span>{loading && !isDraft ? 'Submitting...' : 'Submit'}</span>
        </Button>
      </Stack>
    </Box>
  );
};

export default CreateDuplicateApplication;
